import { Location as LocationIcon, Water } from '@watersport/ui-react-icons'
import { Button, SelectorButton } from '@watersport/ui-react-next'
import React, { useCallback, useEffect, useState } from 'react'

import ActivityLabel from 'features/common/components/ActivityLabel'
import ActivityModal from 'features/common/components/ActivityModal'
import LocationLabel from 'features/common/components/LocationLabel'
import LocationModal from 'features/common/components/LocationModal'
import { cn } from 'features/common/helpers/cn'
import { useFormValidation } from 'features/common/hooks/useFormValidation'
import { Activity, Location, SearchScope } from 'features/common/types'
import LocationMap from 'features/locationMap/LocationMap'

export interface SearchFormProps {
  location?: Location | undefined
  scope?: SearchScope | undefined
  activities?: Activity[] | undefined
  polygons?: [number, number][][]
  onSearch: (
    location: Location | undefined,
    scope: SearchScope | undefined,
    polygons: [number, number][][] | undefined,
    activities: Activity[],
    selectedAllActivities: boolean,
  ) => void
  showLabel?: boolean
}

const SearchForm: React.FC<SearchFormProps> = ({
  onSearch,
  location: pLocation,
  scope: pScope,
  polygons: pPolygons,
  activities: pActivities,
  showLabel = true,
}) => {
  const [showMap, setShowMap] = useState<boolean>(false)
  const [showLocation, setShowLocation] = useState<boolean>(false)
  const [showActivity, setShowActivity] = useState<boolean>(false)
  const [location, setLocation] = useState<Location | undefined>(undefined)
  const [scope, setScope] = useState<SearchScope | undefined>(undefined)
  const [polygons, setPolygons] = useState<[number, number][][] | undefined>(undefined)
  const [activities, setActivities] = useState<Activity[]>([])
  const locationMap = React.useRef<React.ElementRef<typeof LocationMap>>(null)
  const [selectedAll, setSelectedAll] = useState<boolean>(true)

  useEffect(() => {
    setLocation(pLocation)
  }, [pLocation])

  useEffect(() => {
    setScope(pScope)
  }, [pScope])

  useEffect(() => {
    setPolygons(pPolygons)
  }, [pPolygons])

  useEffect(() => {
    setActivities(pActivities ?? [])
  }, [pActivities])

  const validationRules = {
    location: (value: unknown) => (!value ? 'Select location first' : undefined),
    activities: (value: unknown) => {
      const activitiesValue = value as Activity[]
      return activitiesValue.length === 0 ? 'Select water sport(s) first' : undefined
    },
  }

  const { errors, validate, handleValueChange } = useFormValidation(validationRules)

  useEffect(() => {
    handleValueChange({ location: location ?? scope ?? polygons, activities })
  }, [location, activities, handleValueChange, scope, polygons])

  const onSelectScope = useCallback(
    (type: 'select' | 'drawing', s: SearchScope | [number, number][][] | undefined) => {
      if (type === 'select') {
        setScope(s as SearchScope)
        setLocation(undefined)
        setPolygons(undefined)
        setShowLocation(false)
      }

      if (type === 'drawing') {
        setPolygons(s as [number, number][][])
        setLocation(undefined)
        setScope(undefined)
        setShowLocation(false)
      }
    },
    [],
  )

  const onSelectLocation = useCallback((l: Location | undefined) => {
    setLocation(l)
    setScope(undefined)
    setPolygons(undefined)
  }, [])

  const onSelectActivities = useCallback((ac: Activity[], selected: boolean) => {
    setActivities(ac)
    setSelectedAll(selected)
  }, [])

  const handleSearch = useCallback(() => {
    const validationErrors = validate({ location: location ?? scope ?? polygons, activities })
    if (Object.keys(validationErrors).length === 0 && onSearch) {
      onSearch(location, scope, polygons, activities, selectedAll)
    }
  }, [activities, location, onSearch, polygons, scope, selectedAll, validate])

  const onClickMapView = useCallback(() => {
    setShowMap(true)
    if (scope) locationMap.current?.setInitialScope(scope)
    if (polygons) locationMap.current?.setInitialPolygons(polygons)
  }, [polygons, scope])

  return (
    <>
      <div className="flex flex-col gap-5">
        <SelectorButton
          className={'rounded-xl'}
          description={showLabel && (location || scope) ? 'Where?' : undefined}
          placeholder={'Where?'}
          selectedValue={
            (location || scope || polygons) && (
              <LocationLabel location={location} scope={scope} polygons={polygons} />
            )
          }
          icon={
            <LocationIcon
              className={cn('text-blue-700', errors.location && 'text-base-alert')}
              height={24}
              width={24}
            />
          }
          onClick={() => setShowLocation(true)}
          error={errors.location}
          data-testid="location-selector"
        />
        <SelectorButton
          className={'rounded-xl'}
          description={showLabel && activities.length > 0 ? 'Water sports' : undefined}
          placeholder={'Water sports'}
          selectedValue={
            activities.length > 0 && <ActivityLabel location={location} activities={activities} />
          }
          icon={
            <Water
              className={cn('text-blue-700', errors.activities && 'text-base-alert')}
              height={24}
              width={24}
            />
          }
          onClick={() => setShowActivity(true)}
          active={true}
          error={errors.activities}
          data-testid="activities-selector"
        />
      </div>
      <Button
        className={cn('btn-search')}
        containerClassName={cn('mt-10')}
        variant={'default'}
        text={'Search'}
        rounded
        size={'xl'}
        onClick={handleSearch}
        data-testid="search-button"
      />
      <LocationModal
        show={showLocation}
        onSelect={onSelectLocation}
        onClose={() => setShowLocation(false)}
        selectedLocation={location}
        onClickMapView={onClickMapView}
        scope={scope}
        polygons={polygons}
      />
      <ActivityModal
        show={showActivity}
        locationId={location?._id}
        scope={scope}
        polygons={polygons}
        selected={activities}
        onClose={() => setShowActivity(false)}
        onSelect={onSelectActivities}
      />
      <LocationMap
        show={showMap}
        polygons={polygons}
        onClose={() => setShowMap(false)}
        onFinish={onSelectScope}
        ref={locationMap}
      />
    </>
  )
}

export default SearchForm
